import {
    useGetPendingAccountTransfers,
    useTransferAccountOwnership,
    type UserData,
} from "@/api/userQueries"
import { Button } from "@/components/ui/button"
import { Alert, AlertTitle } from "@/components/ui/alert"
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Loader, TriangleAlert, ArrowRight, ChevronDown } from "lucide-react"
import { toast } from "react-hot-toast"
import { useState } from "react"

interface PendingTransfer extends UserData {
    userId: string
}

export const TransferRequestAlert = () => {
    const { data: pending = [], isPending: isLoadingPending } =
        useGetPendingAccountTransfers()
    const transferMutation = useTransferAccountOwnership()
    const [open, setOpen] = useState(true)
    const [processingKey, setProcessingKey] = useState<string | null>(null)

    const handleTransfer = async (
        userId: string,
        action: "accept" | "reject"
    ) => {
        const key = `${userId}-${action}`
        if (processingKey || transferMutation.isPending) return
        setProcessingKey(key)
        try {
            await transferMutation.mutateAsync({ userId, action })
            toast.success(`Transfer ${action}ed successfully.`)
        } catch {
            toast.error(`Failed to ${action} transfer.`)
        } finally {
            setProcessingKey(null)
        }
    }

    if (isLoadingPending || !pending || pending.length === 0) return null

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild>
                <Alert variant="warning" className="mb-6 cursor-pointer">
                    <TriangleAlert className="h-5 w-5" />
                    <AlertTitle className="flex items-center gap-2">
                        New account transfer requests
                        <ChevronDown
                            className={`ml-auto h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
                        />
                    </AlertTitle>
                </Alert>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4">
                {pending.map((transfer: PendingTransfer) => {
                    const req = transfer.accountOwnershipTransferRequest
                    if (!req || !transfer.email) return null
                    return (
                        <div
                            key={transfer.userId}
                            className="bg-white p-4 rounded-md border flex justify-between items-center gap-4"
                        >
                            <div className="flex-1">
                                <p className="font-medium text-sm">
                                    {transfer.firstName} {transfer.lastName}
                                </p>
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    {transfer.email}
                                    <ArrowRight className="h-3 w-3" />
                                    {req.email}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 flex-shrink-0 justify-end">
                                <Button
                                    size="sm"
                                    onClick={() =>
                                        handleTransfer(
                                            transfer.userId,
                                            "accept"
                                        )
                                    }
                                    disabled={
                                        processingKey ===
                                        `${transfer.userId}-accept`
                                    }
                                >
                                    {processingKey ===
                                    `${transfer.userId}-accept` ? (
                                        <Loader className="h-3 w-3 animate-spin" />
                                    ) : (
                                        "Accept Transfer"
                                    )}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() =>
                                        handleTransfer(
                                            transfer.userId,
                                            "reject"
                                        )
                                    }
                                    disabled={
                                        processingKey ===
                                        `${transfer.userId}-reject`
                                    }
                                >
                                    {processingKey ===
                                    `${transfer.userId}-reject` ? (
                                        <Loader className="h-3 w-3 animate-spin" />
                                    ) : (
                                        "Reject Transfer"
                                    )}
                                </Button>
                            </div>
                        </div>
                    )
                })}
            </CollapsibleContent>
        </Collapsible>
    )
}
